/**
 * CannotProvideModal - Record exception for documents that cannot be provided
 *
 * Features:
 * - Exception reason selection
 * - Required justification
 * - Expected resolution date
 * - Optional supporting document upload
 */

import React, { useState } from 'react';
import { X, AlertTriangle, Calendar, Loader2, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { documentValidationAPI } from '../../api.js';
import { CANNOT_PROVIDE_REASONS } from '../../config/complianceRules.js';

/**
 * CannotProvideModal
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - doc: Document object (or null for missing doc)
 * - folderCode: Folder code if document is missing
 * - scope: Scope if document is missing
 * - referenceId: Reference ID if document is missing
 * - onRecorded: (result) => void
 */
export default function CannotProvideModal({
  isOpen,
  onClose,
  doc,
  folderCode,
  scope,
  referenceId,
  onRecorded
}) {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [justification, setJustification] = useState('');
  const [expectedResolutionDate, setExpectedResolutionDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Get final reason
  const reason = selectedReason === 'other' ? customReason : selectedReason;

  // Handle submit
  const handleSubmit = async () => {
    if (!reason) {
      alert(t('documents.cannotProvide.errors.reasonRequired'));
      return;
    }

    if (!justification.trim()) {
      alert(t('documents.cannotProvide.errors.justificationRequired'));
      return;
    }

    setSubmitting(true);
    try {
      // For existing documents, mark as cannot_provide
      if (doc) {
        const result = await documentValidationAPI.cannotProvide(doc.id, {
          justification: `${reason}: ${justification}`,
          expected_resolution_date: expectedResolutionDate || null
        });

        if (result.ok) {
          onRecorded?.(result.data);
          onClose();
        } else {
          alert(result.error?.message || t('documents.cannotProvide.errors.saveFailed'));
        }
      } else {
        // For missing documents, we would create a placeholder record
        // This is handled differently - typically through document_requests
        alert(t('documents.cannotProvide.errors.missingDocNotSupported'));
      }
    } catch (err) {
      console.error('Cannot provide error:', err);
      alert(t('documents.messages.errorPrefix', { message: err.message }));
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate minimum date (today)
  const minDate = new Date().toISOString().split('T')[0];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-yellow-50">
          <h2 className="text-lg font-semibold text-yellow-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {t('documents.cannotProvide.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-yellow-400 hover:text-yellow-600 hover:bg-yellow-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Warning */}
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            {t('documents.cannotProvide.warning')}
          </div>

          {/* Document info */}
          {doc && (
            <div className="text-sm text-gray-600">
              {t('documents.cannotProvide.documentLabel')} <span className="font-medium">{doc.file_name}</span>
            </div>
          )}
          {!doc && folderCode && (
            <div className="text-sm text-gray-600">
              {t('documents.cannotProvide.documentTypeLabel')} <span className="font-medium">{folderCode}</span>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('documents.cannotProvide.reasonLabel')} <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {CANNOT_PROVIDE_REASONS.map(reason => (
                <label key={reason.code} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="cannot_provide_reason"
                    value={reason.code}
                    checked={selectedReason === reason.code}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="text-yellow-600 focus:ring-yellow-500"
                  />
                  <span className="text-sm text-gray-700">{t(`documents.cannotProvide.reasons.${reason.code}`, { defaultValue: reason.label })}</span>
                </label>
              ))}
            </div>

            {/* Custom reason input */}
            {selectedReason === 'other' && (
              <input
                type="text"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder={t('documents.cannotProvide.customReasonPlaceholder')}
                className="mt-2 w-full px-3 py-2 border rounded text-sm"
              />
            )}
          </div>

          {/* Justification */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('documents.cannotProvide.justificationLabel')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder={t('documents.cannotProvide.justificationPlaceholder')}
              className="w-full px-3 py-2 border rounded text-sm"
              rows={3}
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('documents.cannotProvide.justificationHint')}
            </p>
          </div>

          {/* Expected resolution date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {t('documents.cannotProvide.resolutionDateLabel')}
            </label>
            <input
              type="date"
              value={expectedResolutionDate}
              onChange={(e) => setExpectedResolutionDate(e.target.value)}
              min={minDate}
              className="w-full px-3 py-2 border rounded text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('documents.cannotProvide.resolutionDateHint')}
            </p>
          </div>

          {/* Supporting document notice */}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 flex items-start gap-2">
            <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">{t('documents.cannotProvide.supportingTitle')}</p>
              <p className="text-xs mt-1">
                {t('documents.cannotProvide.supportingHint')}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
          >
            {t('common.actions.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !reason || !justification.trim()}
            className="px-4 py-2 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <AlertTriangle className="w-4 h-4" />
            )}
            {t('documents.cannotProvide.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
